import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

export interface TableWizardProps {
  open: boolean;
  theme?: string;
  onCreate: (size: { rows: number; cols: number }) => void;
  onCancel: () => void;
}

export function TableWizard({
  open,
  theme: themeProp,
  onCreate,
  onCancel,
}: TableWizardProps) {
  const { theme: globalTheme } = useTheme();
  const theme = themeProp ?? globalTheme;

  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [hoverRows, setHoverRows] = useState(0);
  const [hoverCols, setHoverCols] = useState(0);
  const [useCustomSize, setUseCustomSize] = useState(false);

  const maxGridRows = 10;
  const maxGridCols = 10;

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setRows(3);
      setCols(3);
      setHoverRows(0);
      setHoverCols(0);
      setUseCustomSize(false);
    }
  }, [open]);

  const dialogClasses = useMemo(() => {
    const classes = ['kst-dialog', 'kst-table-wizard'];
    if (theme) classes.push(theme);
    return classes.join(' ');
  }, [theme]);

  const gridCells = useMemo(() => {
    const cells: { row: number; col: number; active: boolean }[] = [];
    for (let r = 1; r <= maxGridRows; r++) {
      for (let c = 1; c <= maxGridCols; c++) {
        cells.push({
          row: r,
          col: c,
          active: r <= hoverRows && c <= hoverCols,
        });
      }
    }
    return cells;
  }, [hoverRows, hoverCols]);

  const gridLabel = useMemo(() => {
    if (hoverRows && hoverCols) {
      return hoverRows + ' x ' + hoverCols;
    }
    return 'Select size';
  }, [hoverRows, hoverCols]);

  const handleGridHover = useCallback((row: number, col: number) => {
    setHoverRows(row);
    setHoverCols(col);
  }, []);

  const handleGridLeave = useCallback(() => {
    setHoverRows(0);
    setHoverCols(0);
  }, []);

  const handleClose = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleGridClick = useCallback(
    (row: number, col: number) => {
      onCreate({ rows: row, cols: col });
      handleClose();
    },
    [onCreate, handleClose]
  );

  const handleCustomCreate = useCallback(() => {
    if (rows < 1 || cols < 1) return;
    onCreate({ rows, cols });
    handleClose();
  }, [rows, cols, onCreate, handleClose]);

  const handleKeydown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  if (!open) return null;

  return (
    <div
      className="kst-dialog-overlay"
      onClick={handleBackdropClick}
      onKeyDown={handleKeydown}
    >
      <div className={dialogClasses} role="dialog" aria-labelledby="table-wizard-title">
        <div className="kst-dialog-header">
          <h2 id="table-wizard-title">Insert Table</h2>
          <button
            className="kst-dialog-close"
            onClick={handleClose}
            aria-label="Close"
            type="button"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="kst-dialog-body">
          {!useCustomSize ? (
            <div className="table-grid-picker">
              <div className="grid-label">{gridLabel}</div>
              <div
                className="table-grid"
                onMouseLeave={handleGridLeave}
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${maxGridCols}, 1fr)`,
                  gap: '2px',
                }}
              >
                {gridCells.map((cell) => (
                  <div
                    key={`${cell.row}-${cell.col}`}
                    className={`grid-cell${cell.active ? ' active' : ''}`}
                    onMouseEnter={() => handleGridHover(cell.row, cell.col)}
                    onClick={() => handleGridClick(cell.row, cell.col)}
                  ></div>
                ))}
              </div>
              <button
                className="kst-btn-link"
                onClick={() => setUseCustomSize(true)}
                type="button"
              >
                Custom size...
              </button>
            </div>
          ) : (
            <div className="custom-size-form">
              <div className="kst-form-row">
                <div className="kst-form-group half">
                  <label htmlFor="table-rows">Rows</label>
                  <input
                    id="table-rows"
                    type="number"
                    value={rows}
                    onChange={(e) => setRows(Number(e.target.value))}
                    min={1}
                    max={100}
                  />
                </div>
                <div className="kst-form-group half">
                  <label htmlFor="table-cols">Columns</label>
                  <input
                    id="table-cols"
                    type="number"
                    value={cols}
                    onChange={(e) => setCols(Number(e.target.value))}
                    min={1}
                    max={100}
                  />
                </div>
              </div>
              <button
                className="kst-btn-link"
                onClick={() => setUseCustomSize(false)}
                type="button"
              >
                Use grid picker
              </button>
            </div>
          )}
        </div>
        {useCustomSize && (
          <div className="kst-dialog-footer">
            <button
              className="kst-btn kst-btn-secondary"
              onClick={handleClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className="kst-btn kst-btn-primary"
              onClick={handleCustomCreate}
              disabled={rows < 1 || cols < 1}
              type="button"
            >
              Create Table
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

(window as any).KstTableWizardReact = TableWizard;
